// layout
import {
  Filters,
  FiltersLayout,
  QuickFilter,
  DateRangeFilter,
  DataGridLayout,
  ListHeader,
  ListPageLayout,
  useListParamsMigration,
  List,
} from 'framework/list';
// buttons
import EditLeaveButton from 'domain/people/leave/shared/button/EditLeaveButton';
// fields
import LeaveTypeField from 'domain/people/leave/shared/field/LeaveTypeField';
import LeaveStatusField from 'domain/people/leave/shared/field/LeaveStatusField';
// filters
import LeaveStatusFilter from 'domain/people/leave/List/filter/LeaveStatusFilter';
import LeaveTypeFilter from 'domain/people/leave/List/filter/LeaveTypeFilter';
import { EmployeePositionFilter, OfficeFilterById, EmployeeFilter } from 'domain/shared/employee';
import LeaveWorkerField from 'domain/people/leave/shared/field/LeaveWorkerField';
import useLeaveService from 'domain/people/leave/shared/leaveService';
import { useLeaveUrl } from 'domain/people/leave/leaveRouting';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { BadgeField, DateField, FullDateField, SearchLinkField, TextField } from 'framework/ui/field';
import { useOneStopRequestsUrl } from 'domain/shared/oneStop';
import { useAsyncValue } from 'framework/utils';
import { CREATE_EXPENSE } from 'domain/shared/expense';
import { AddLeaveCompensationButton, isLeaveCompensationButtonVisible } from 'domain/shared/leave';
import ExcludedFromBalanceBadge from '../shared/badge/ExcludedFromBalanceBadge';
import {
  LEAVE_STATUSES_ALIASES,
  LEAVES_CREATE,
  LEAVES_EXTEND,
  LEAVES_REQUEST,
  LEAVES_UPDATE,
  LeaveDtoWithCompensation,
} from 'domain/shared/leave';
import { CreateButton } from 'framework/ui/ui-builder';
import { useAuthorization } from 'framework/authorization';
import { Data } from 'framework/types';

const LeavesList = () => {
  const { searchWithExpensesInfo, canBeExtended, getFiltersValidationSchema } = useLeaveService();
  const { isGranted } = useAuthorization();
  const { getUserAlias } = useAuthenticatedUserService();
  const userAlias = useAsyncValue(() => getUserAlias());
  const { getCreateUrl, getSingleEntityUrl } = useLeaveUrl();
  const { getSingleEntityUrl: getSingleOneStopEntityUrl } = useOneStopRequestsUrl();
  const canAddCompensation = isGranted(CREATE_EXPENSE);

  const migrationsArray = [
    {
      version: '1.0',
      migration: {
        filter: userAlias ? { 'employee.alias': { eq: [userAlias] } } : {},
        sort: { field: 'startDate', order: 'DESC' },
      },
    },
  ];

  useListParamsMigration(migrationsArray as any);

  const isUpdateButtonVisible = (leave: Data<LeaveDtoWithCompensation>) => {
    return leave.canEdit && (isGranted(LEAVES_UPDATE) || (isGranted(LEAVES_EXTEND) && canBeExtended(leave)));
  };

  const filters = (
    <Filters getValidationSchema={getFiltersValidationSchema}>
      <FiltersLayout
        quickFilters={[
          <QuickFilter
            key="active-leaves"
            label="Leaves in approval"
            state={{ status: { in: [LEAVE_STATUSES_ALIASES.IN_APPROVAL] } }}
          />,
        ]}
      >
        <LeaveTypeFilter label="Type" source="specification:leaveSubType" />
        <LeaveStatusFilter label="Status" source="status" />
        <DateRangeFilter label="Start Date" source="startDate" canBeInFuture />
        <DateRangeFilter label="End Date" source="actualEndDate" canBeInFuture />
        <EmployeeFilter label="Worker" source="employee.alias" />
        <EmployeePositionFilter label="Position" source="employee.positionId" />
        <OfficeFilterById label="Office" source="specification:leaveByOfficeSpecification" />
      </FiltersLayout>
    </Filters>
  );

  return (
    <List
      getDataMethod={searchWithExpensesInfo}
      defaultFilter={userAlias ? { employeeAlias: { eq: [userAlias] } } : {}}
      defaultSortOrder="DESC"
      defaultSortField="startDate"
    >
      <ListPageLayout
        header={<ListHeader label="Leaves" icon="fa-ship" />}
        actions={[
          <CreateButton
            url={getCreateUrl()}
            label="Create leave"
            key="create-leave"
            permissionToCheck={LEAVES_CREATE}
          />,
          <CreateButton
            url={getSingleOneStopEntityUrl('HR03')}
            label="Request leave via One-Stop"
            key="request-leave"
            permissionToCheck={LEAVES_REQUEST}
            sx={{ marginLeft: '20px' }}
          />,
        ]}
        filter={filters}
      >
        <DataGridLayout
          buttons={[
            <EditLeaveButton
              key="edit-leave"
              source="id"
              outline
              isVisible={isUpdateButtonVisible}
              permissionToCheck={null}
            />,
            <AddLeaveCompensationButton isVisible={isLeaveCompensationButtonVisible} />,
          ]}
          sxTableWrapper={{ '& th:last-child': { width: canAddCompensation ? '176px' : 0 } }}
        >
          <SearchLinkField
            source="id"
            url={(id: Nullable<StringOrNumber> | undefined) => getSingleEntityUrl(id)}
            label="Leave ID"
            isSortable
          />
          <LeaveWorkerField source="employeeAlias" label="Worker" sortSource="employee.nameEn.lastName" isSortable />
          <LeaveTypeField source="type" isSortable />
          <BadgeField
            badges={[
              <ExcludedFromBalanceBadge
                isVisible={({ balanceInfo: { isExcludedFromBalance } }: LeaveDtoWithCompensation) =>
                  isExcludedFromBalance
                }
              />,
            ]}
            sortSource="startDate"
            label="Start date"
            isSortable
          >
            <DateField source="period.startDate" />
          </BadgeField>
          <DateField sortSource="actualEndDate" source="period.actualEndDate" label="End date" isSortable />
          <TextField source="period.duration" label="Duration, days" />
          <LeaveStatusField source="status" isSortable />
          <FullDateField source="updatedAt" label="Last modified date" isSortable />
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default LeavesList;
